import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AvatarImage } from "@/components/ui/DefaultAvatar";
import { Loader2, Users, UserPlus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "@/stores/user/authStore";
import { userService } from "@/services/userService";
import { useToast } from "@/hooks/use-toast";
import { Connection, ConnectionsData } from "@/types/social";
import { cn } from "@/lib/utils";

interface ConnectionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  activeTab: "followers" | "following";
  setActiveTab: React.Dispatch<React.SetStateAction<"followers" | "following">>;
  isPrivate?: boolean;
  isCurrentUser: boolean;
  isFollowing?: boolean;
  connections: ConnectionsData | null;
  setConnections: React.Dispatch<React.SetStateAction<ConnectionsData | null>>;
  isLoading: boolean;
  onUnfollow?: (userId: string) => void;
}

export const ConnectionsModal: React.FC<ConnectionsModalProps> = ({
  isOpen,
  onClose,
  activeTab,
  setActiveTab,
  isPrivate = false,
  isCurrentUser,
  isFollowing = false,
  connections,
  setConnections,
  isLoading: isInitialLoading,
  onUnfollow,
}) => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();
  const { toast } = useToast();

  const [pendingFollows, setPendingFollows] = React.useState<Set<string>>(
    new Set()
  );

  const handleUserClick = (userId: string) => {
    onClose();
    navigate(`/account/${userId}`);
  };

  const handleFollow = async (userId: string) => {
    setPendingFollows((prev) => new Set(prev).add(userId));
    try {
      await userService.followUser(userId);
      // Update local state
      setConnections((prev) => {
        if (!prev) return prev;
        const targetConnection = prev[activeTab].find(
          (c) => c.user_id === userId
        );
        const isPrivate = targetConnection?.is_private || false;

        const result = {
          ...prev,
          [activeTab]: prev[activeTab].map((connection) =>
            connection.user_id === userId
              ? {
                  ...connection,
                  is_following: !connection.is_private,
                  is_requested: connection.is_private,
                }
              : connection
          ),
        };

        toast({
          title: "Success",
          description: isPrivate
            ? "Follow request sent"
            : "Successfully followed user",
        });

        return result;
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to follow user",
        variant: "destructive",
      });
    } finally {
      setPendingFollows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const handleUnfollow = async (userId: string) => {
    setPendingFollows((prev) => new Set(prev).add(userId));
    try {
      await userService.unfollowUser(userId);
      // Update local state
      setConnections((prev) => {
        if (!prev) return prev;

        // If we're in the following tab, remove the user from the list
        if (activeTab === "following") {
          return {
            ...prev,
            following: prev.following.filter((conn) => conn.user_id !== userId),
          };
        } else {
          // If we're in the followers tab, just update the status
          return {
            ...prev,
            [activeTab]: prev[activeTab].map((connection) =>
              connection.user_id === userId
                ? {
                    ...connection,
                    is_following: false,
                    is_requested: false,
                  }
                : connection
            ),
          };
        }
      });

      // Call the onUnfollow callback if provided
      if (onUnfollow) {
        onUnfollow(userId);
      }

      toast({
        title: "Success",
        description: "Successfully unfollowed user",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to unfollow user",
        variant: "destructive",
      });
    } finally {
      setPendingFollows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  if (!isCurrentUser && isPrivate && !isFollowing) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent
          className="sm:max-w-[425px]"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Private Account</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm font-light text-neutral-600">
              This account is private. Follow this user to see their
              connections.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const currentConnections =
    activeTab === "followers" ? connections?.followers : connections?.following;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="sm:max-w-[425px] h-[450px] flex flex-col px-6 pt-6 pb-2"
        hideCloseButton
        aria-describedby="connections-modal-description"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DialogTitle className="text-lg font-semibold mb-2 flex items-center space-x-3">
          <span>{activeTab === "followers" ? "Followers" : "Following"}</span>
          <div className="w-1 h-1 bg-neutral-500 rounded-full"></div>
          {currentConnections?.length && currentConnections.length > 0 ? (
            <span className="text-sm text-neutral-500 font-normal">
              {currentConnections.length}
            </span>
          ) : (
            <span className="text-sm text-neutral-500 font-normal">0</span>
          )}
        </DialogTitle>
        <DialogHeader className="pb-4">
          <div className="flex space-x-1 rounded-lg bg-neutral-100 p-1">
            <button
              className={cn(
                "flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors flex items-center justify-center gap-2",
                activeTab === "followers"
                  ? "bg-white text-neutral-900 shadow-sm"
                  : "text-neutral-600 hover:text-neutral-900"
              )}
              onClick={() => setActiveTab("followers")}
            >
              <Users className="h-4 w-4" strokeWidth={1.5} />
              Followers
            </button>
            <button
              className={cn(
                "flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors flex items-center justify-center gap-2",
                activeTab === "following"
                  ? "bg-white text-neutral-900 shadow-sm"
                  : "text-neutral-600 hover:text-neutral-900"
              )}
              onClick={() => setActiveTab("following")}
            >
              <UserPlus className="h-4 w-4" strokeWidth={1.5} />
              Following
            </button>
          </div>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar pr-2">
          {isInitialLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-neutral-500" />
            </div>
          ) : currentConnections && currentConnections.length > 0 ? (
            <div className="space-y-2">
              {currentConnections.map((connection: Connection) => (
                <div
                  key={connection.user_id}
                  className="flex items-center justify-between p-2.5 hover:bg-neutral-50 rounded-lg transition-colors"
                >
                  <div
                    className="flex items-center gap-3 cursor-pointer flex-1"
                    onClick={() => handleUserClick(connection.user_id)}
                  >
                    <AvatarImage
                      src={connection.profile_image}
                      alt={connection.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-medium">{connection.name}</p>
                      <p className="text-xs text-neutral-500">
                        @{connection.username}
                      </p>
                    </div>
                  </div>
                  {currentUser?.user_id !== connection.user_id && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        connection.is_following || connection.is_requested
                          ? handleUnfollow(connection.user_id)
                          : handleFollow(connection.user_id);
                      }}
                      disabled={pendingFollows.has(connection.user_id)}
                      className={`ml-2 px-4 py-1.5 text-xs rounded-lg transition-colors ${
                        connection.is_following || connection.is_requested
                          ? "bg-neutral-100 hover:bg-neutral-200 text-neutral-700"
                          : "bg-indigo-600 hover:bg-indigo-700 text-white"
                      }`}
                    >
                      {pendingFollows.has(connection.user_id) ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : connection.is_requested ? (
                        "Requested"
                      ) : connection.is_following ? (
                        "Following"
                      ) : (
                        "Follow"
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-sm text-neutral-600">No {activeTab} yet</p>
            </div>
          )}
        </div>
        <div className="pt-4 pb-2 border-t">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 text-sm font-medium text-neutral-700 bg-neutral-100 rounded-lg hover:bg-neutral-200 transition-colors"
          >
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
